import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Monitor, CheckCircle } from 'lucide-react';

/**
 * Botón/Banner para instalar la PWA
 * Detecta automáticamente si la app se puede instalar
 */
const InstallPWAButton = ({ variant = 'banner' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Verificar si ya está instalada
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Verificar si el usuario ya rechazó el banner
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
        return;
      }
    }

    // Escuchar el evento beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Detectar cuando se instala
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Mostrar el prompt de instalación
    deferredPrompt.prompt();
    
    // Esperar la respuesta del usuario
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setDismissed(true);
    setShowInstallBanner(false);
  };

  // Si ya está instalada, mostrar badge de éxito
  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-medium">
        <CheckCircle size={14} />
        <span>App Instalada</span>
      </div>
    );
  }

  // Si no hay prompt disponible o fue rechazado, no mostrar nada
  if (!showInstallBanner || dismissed || !deferredPrompt) {
    return null;
  }

  // Variante: Botón compacto
  if (variant === 'button') {
    return (
      <button
        onClick={handleInstall}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gold-500 to-zinc-500 hover:from-gold-400 hover:to-zinc-400 text-white rounded-lg font-medium text-sm transition-all shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40"
      >
        <Download size={16} />
        <span>Instalar App</span>
      </button>
    );
  }

  // Variante: Banner flotante (por defecto)
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[9999] animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-gold-500/30 rounded-2xl shadow-2xl shadow-gold-500/10 overflow-hidden">
        {/* Barra superior decorativa */}
        <div className="h-1 bg-gradient-to-r from-gold-500 via-zinc-500 to-zinc-500" />
        
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-gold-500/20 to-zinc-500/20 rounded-xl flex items-center justify-center border border-gold-500/30">
                <Download size={24} className="text-gold-400" />
              </div>
              <div>
                <h3 className="font-bold text-white text-base">Instalar Aplicación</h3>
                <p className="text-xs text-slate-400">Acceso rápido desde tu escritorio</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg text-slate-500 hover:text-slate-300 transition-colors"
              title="Cerrar"
            >
              <X size={18} />
            </button>
          </div>

          {/* Beneficios */}
          <div className="flex gap-4 mb-4 text-xs text-slate-400">
            <div className="flex items-center gap-1.5">
              <Monitor size={14} className="text-gold-400" />
              <span>Sin navegador</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Smartphone size={14} className="text-green-400" />
              <span>Funciona offline</span>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-2">
            <button
              onClick={handleInstall}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gold-500 to-zinc-500 hover:from-gold-400 hover:to-zinc-400 text-white rounded-xl font-semibold text-sm transition-all shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40"
            >
              <Download size={18} />
              <span>Instalar Ahora</span>
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-xl font-medium text-sm transition-colors"
            >
              Luego
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPWAButton;
